/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import { Pressable, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { KeyboardAvoidingView, type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles';
import { expensesTicket } from './expensesTicket.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { ExpenseAmountInput } from './ExpenseAmountInput';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { formatExpenseMoney, formatExpenseDateShort, expenseAmountResidual } from '../domain/expenseLabels';
import type { ExpenseRow } from '../domain/expenseTypes';
import { expenseErpCopy } from '../domain/expenseErpCopy';
import { ExpensePaymentFields } from './ExpensePaymentFields';
import {
  canSubmitExpensePayment,
  defaultPaidDateForForm,
  getExpensePaymentSubmitBlockReason,
  resolveSettleMethodFromPaymentForm,
  type ActiveCashSessionInfo,
  type ExpenseAlreadyPaidKind,
  type ExpensePaidFormChoice,
} from './expensePaymentFlow';
import { ExpensePaymentSubmitHint } from './ExpensePaymentSubmitHint';
import type { ExpenseSettleMethod } from './expenseSettlementMethods';
import type { CheckRow } from '../../cash/infra-api';
import { formatMoney } from '../../shared/format/public-api';
import { todayIsoDate } from '../infra/expenseListSql';

type Props = {
  visible: boolean;
  expense: ExpenseRow | null;
  amountText: string;
  onAmountTextChange: (value: string) => void;
  activeCashSession: ActiveCashSessionInfo | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (input: { method: ExpenseSettleMethod; note: string; paidDate: string; checkId?: string | null }) => void;
  onOpenCash?: () => void;
  /** Cheques en cartera para endosar (Fase C). Vacío/undefined = sin opción. */
  checkOptions?: CheckRow[];
  checksLoading?: boolean;
  canPayWithCheck?: boolean;
};

export function ExpenseSettleModal({
  visible,
  expense,
  amountText,
  onAmountTextChange,
  activeCashSession,
  submitting,
  onClose,
  onConfirm,
  onOpenCash,
  checkOptions,
  checksLoading = false,
  canPayWithCheck = false,
}: Props) {
  const backdropPadding = useModalBackdropPadding();
  const { width: winW, height: winH } = useWindowDimensions();
  const isMobile = winW < CONTENT_WIDE_BREAKPOINT;
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const [paidForm, setPaidForm] = useState<ExpensePaidFormChoice | null>(null);
  const [alreadyPaidKind, setAlreadyPaidKind] = useState<ExpenseAlreadyPaidKind | null>(null);
  const [nonDrawerMethod, setNonDrawerMethod] = useState<ExpenseSettleMethod>('transfer');
  const [settleNote, setSettleNote] = useState('');
  const [paidDate, setPaidDate] = useState('');
  const [pastCashDrawerConfirmed, setPastCashDrawerConfirmed] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [negativeAmountWarned, setNegativeAmountWarned] = useState(false);

  // ── Cambios sin guardar: baseline = snapshot de los campos tras el reset de
  // apertura. Mismo patrón que ExpenseRegisterModal.
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const [baselineKey, setBaselineKey] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const activeCashSessionId = activeCashSession?.session.id ?? null;
  const expenseDate = expense?.expenseDate ?? '';

  useEffect(() => {
    if (!visible || !expense) return;
    const initialPaidForm: ExpensePaidFormChoice = activeCashSessionId ? 'cash_drawer_now' : 'transfer';
    const initialPaidDate = defaultPaidDateForForm(expense.expenseDate);
    setPaidForm(initialPaidForm);
    setAlreadyPaidKind(null);
    setNonDrawerMethod('transfer');
    setSettleNote('');
    setPaidDate(initialPaidDate);
    setPastCashDrawerConfirmed(false);
    setSelectedCheckId(null);
    setNegativeAmountWarned(false);
    setDiscardConfirmOpen(false);
    setBaselineKey(
      JSON.stringify({
        amountText,
        paidForm: initialPaidForm,
        alreadyPaidKind: null,
        nonDrawerMethod: 'transfer',
        settleNote: '',
        paidDate: initialPaidDate,
        pastCashDrawerConfirmed: false,
        selectedCheckId: null,
      })
    );
    // amountText lo pone el padre al abrir — va en el baseline del snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, expense?.id, expense?.expenseDate, activeCashSessionId]);

  const settleMethod = useMemo(
    () =>
      paidForm
        ? resolveSettleMethodFromPaymentForm({
            paidForm,
            alreadyPaidKind,
            nonDrawerMethod,
          })
        : 'transfer',
    [paidForm, alreadyPaidKind, nonDrawerMethod]
  );

  const residual = expense ? expenseAmountResidual(expense.amount, expense.amountPaid) : 0;
  const parsedAmount = parseDecimalInput(amountText, { min: 0.01 });
  const payingWithCheck = settleMethod === 'check';

  const paymentSubmitInput = useMemo(
    () => ({
      paymentStatus: 'paid' as const,
      paidForm,
      alreadyPaidKind,
      settleMethod,
      activeCashSessionId,
      settleNote,
      pastCashDrawerConfirmed,
      expenseDate,
      amount: payingWithCheck ? undefined : parsedAmount ?? 0,
      residual,
      checkId: selectedCheckId,
    }),
    [
      paidForm,
      alreadyPaidKind,
      settleMethod,
      activeCashSessionId,
      settleNote,
      pastCashDrawerConfirmed,
      expenseDate,
      payingWithCheck,
      parsedAmount,
      residual,
      selectedCheckId,
    ]
  );

  const submitBlockReason = useMemo(
    () => (submitting ? null : getExpensePaymentSubmitBlockReason(paymentSubmitInput)),
    [submitting, paymentSubmitInput]
  );

  const confirmDisabled = submitting || submitBlockReason != null;

  const formSnapshot = () =>
    JSON.stringify({
      amountText,
      paidForm,
      alreadyPaidKind,
      nonDrawerMethod,
      settleNote,
      paidDate,
      pastCashDrawerConfirmed,
      selectedCheckId,
    });
  const isDirty = formSnapshot() !== baselineKey;

  const handleClose = () => {
    if (submitting) return;
    // Si el confirm ya está arriba, back/Escape lo cierra a él.
    if (discardConfirmOpen) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: 'Descartar pago',
      message: 'Hay datos cargados sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
      presentation: 'overlay',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  };

  if (!expense) return null;

  const expenseLabel = expense.supplierName ?? expense.categoryName ?? expense.categorySlug ?? 'Factura';
  // Proto: "Saldo $24.500 de $48.500 · venció 02/10" / "· pago con cheque".
  const headerSubtitle = payingWithCheck
    ? `Saldo ${formatExpenseMoney(residual)} · pago con cheque`
    : [
        `Saldo ${formatExpenseMoney(residual)} de ${formatExpenseMoney(expense.amount)}`,
        expense.dueDate
          ? `${expense.dueDate < todayIsoDate() ? 'venció' : 'vence'} ${formatExpenseDateShort(expense.dueDate)}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ');

  const selectedCheck = payingWithCheck
    ? (checkOptions ?? []).find((c) => c.id === selectedCheckId) ?? null
    : null;

  const formFieldsElement = (
    <>
      {payingWithCheck ? (
        <View style={modalExperience.infoPanel}>
          <Text style={styles.infoText}>
            El pago sale por el monto del cheque elegido — no sale plata de la caja.
          </Text>
        </View>
      ) : (
        <ModalFormField label="Monto a pagar" required>
          <ExpenseAmountInput
            value={amountText}
            onChangeText={onAmountTextChange}
            onNegativeAttempt={() => setNegativeAmountWarned(true)}
            placeholder="0"
            style={modalExperience.formInput}
            placeholderTextColor={ticketColors.ink3}
            editable={!submitting}
            testID="gastos.settle.amount"
          />
          {negativeAmountWarned ? (
            <Text style={styles.fieldWarning} testID="gastos.settle.negativeAmountHint">
              {expenseErpCopy.negativeAmountHint}
            </Text>
          ) : (
            <Text style={styles.fieldHint}>No puede pasar el saldo de la factura.</Text>
          )}
        </ModalFormField>
      )}

      <ExpensePaymentFields
        expenseDate={expenseDate}
        paymentStatus="paid"
        onPaymentStatusChange={() => {}}
        hidePaymentStatus
        paidForm={paidForm}
        onPaidFormChange={setPaidForm}
        alreadyPaidKind={alreadyPaidKind}
        onAlreadyPaidKindChange={setAlreadyPaidKind}
        nonDrawerMethod={nonDrawerMethod}
        onNonDrawerMethodChange={setNonDrawerMethod}
        settleNote={settleNote}
        onSettleNoteChange={setSettleNote}
        paidDate={paidDate}
        onPaidDateChange={setPaidDate}
        activeCashSession={activeCashSession}
        pastCashDrawerConfirmed={pastCashDrawerConfirmed}
        onPastCashDrawerConfirmedChange={setPastCashDrawerConfirmed}
        submitting={submitting}
        onOpenCash={onOpenCash}
        extraPaidFormOptions={
          canPayWithCheck ? [{ id: 'check', label: 'Cheque en cartera' }] : undefined
        }
      />

      {payingWithCheck ? (
        <ModalFormField label="Cheque a endosar" required>
          {checksLoading ? (
            <Text style={styles.infoText}>Cargando cheques…</Text>
          ) : (checkOptions ?? []).length === 0 ? (
            <Text style={styles.infoText}>No hay cheques en cartera.</Text>
          ) : (
            <View style={{ gap: spacing.xs }} testID="gastos.settle.check-list">
              {(checkOptions ?? []).map((check) => {
                const active = check.id === selectedCheckId;
                return (
                  <TouchableOpacity
                    key={check.id}
                    onPress={() => setSelectedCheckId(check.id)}
                    disabled={submitting}
                    accessibilityRole="button"
                    accessibilityLabel={`Cheque ${check.number}`}
                    style={[
                      modalExperience.entitySelector,
                      active && styles.checkOptionActive,
                    ]}
                    testID={`gastos.settle.check.${check.id}`}
                  >
                    <View style={{ flex: 1, gap: 2 }}>
                      <Text style={modalExperience.filterSelectValue} numberOfLines={1}>
                        N° {check.number} · {formatMoney(check.amount)}
                      </Text>
                      <Text style={styles.checkOptionSub} numberOfLines={1}>
                        {[check.drawer, check.bank, check.dueDate ? `vence ${formatExpenseDateShort(check.dueDate)}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    </View>
                    {active ? <Ionicons name="checkmark" size={18} color={ticketColors.gold} /> : null}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
          {selectedCheck ? (
            <Text style={[styles.infoText, { marginTop: spacing.xs }]}>
              Se endosa por {formatMoney(selectedCheck.amount)}.
            </Text>
          ) : null}
        </ModalFormField>
      ) : null}
      {payingWithCheck && selectedCheck && selectedCheck.amount > residual ? (
        <View style={modalExperience.warningPanel}>
          <Text style={styles.infoText}>
            El cheque cubre más que el saldo ({formatExpenseMoney(residual)}). La diferencia queda a favor.
          </Text>
        </View>
      ) : null}
    </>
  );

  // ── Mobile: bottom sheet (View shell — Pressable envolviendo el card bloquea scroll) ──
  if (isMobile) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose} accessibilityViewIsModal>
          <View style={ticketChrome.sheetBackdrop}>
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            />
            <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
            <View style={[ticketChrome.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
              <View style={ticketChrome.grabber} />

              <View style={ticketChrome.sheetHeader}>
                <Text style={ticketChrome.sheetSubtitle}>{expenseErpCopy.registerPaymentTitle}</Text>
                <TouchableOpacity
                  onPress={handleClose}
                  style={ticketChrome.sheetCloseBtn}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
                </TouchableOpacity>
              </View>

              <ModalAwareScrollView
                ref={scrollRef}
                style={styles.sheetScroll}
                contentContainerStyle={styles.sheetScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
                bottomOffset={spacing.sm}
              >
                <View ref={scrollContentRef} collapsable={false}>
                  <View style={expensesTicket.heroBand}>
                    <Text style={expensesTicket.heroKicker}>PAGO</Text>
                    <Text style={expensesTicket.heroSubtitle} numberOfLines={3} ellipsizeMode="tail">
                      {headerSubtitle}
                    </Text>
                  </View>
                  {formFieldsElement}
                </View>
              </ModalAwareScrollView>

              <View style={ticketChrome.foot}>
                <View style={{ width: '100%', gap: spacing.xs }}>
                  <ExpensePaymentSubmitHint message={submitBlockReason} />
                  <View style={{ flexDirection: 'row', gap: spacing.xs, width: '100%' }}>
                    <TouchableOpacity
                      style={[ticketChrome.btnGhost, { flex: 1, backgroundColor: ticketColors.paper2 }]}
                      onPress={handleClose}
                      disabled={submitting}
                      accessibilityRole="button"
                      accessibilityLabel="Cancelar"
                    >
                      <Text style={[ticketChrome.btnGhostText, { color: ticketColors.ink }]}>Cancelar</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[ticketChrome.btnGold, { flex: 1 }, confirmDisabled && posUi.disabledCard]}
                      onPress={() =>
                        onConfirm({
                          method: settleMethod,
                          note: settleNote,
                          paidDate,
                          checkId: payingWithCheck ? selectedCheckId : null,
                        })
                      }
                      disabled={confirmDisabled}
                      accessibilityRole="button"
                      accessibilityLabel="Confirmar pago"
                      testID="gastos.settle.confirm"
                    >
                      <Text style={ticketChrome.btnGoldText}>{submitting ? 'Guardando…' : 'Confirmar pago'}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
            </KeyboardAvoidingView>
            {DiscardConfirmModal}
          </View>
      </Modal>
    );
  }

  // ── Wide: centered card modal ──
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose} accessibilityViewIsModal>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthStandard,
            { gap: spacing.sm, width: '100%', alignSelf: 'center', maxHeight: '96%' },
          ]}
        >
          <ModalScreenHeader
            kicker="Gastos · Registrar pago"
            title={expenseLabel}
            subtitle={headerSubtitle}
            onClose={handleClose}
          />

          <ModalFormScroll
            ref={scrollRef}
            contentRef={scrollContentRef}
            contentContainerStyle={modalExperience.formPanel}
            keyboardBottomPadding={spacing.sm}
          >
            {formFieldsElement}
          </ModalFormScroll>

          <View style={{ width: '100%', gap: spacing.xs }}>
            <ExpensePaymentSubmitHint message={submitBlockReason} />
            <View style={[posUi.modalActions, modalExperience.footerActions]}>
              <TouchableOpacity
                style={[posUi.secondaryButton, posUi.modalButtonHalf]}
                onPress={handleClose}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Cancelar"
              >
                <Text style={posUi.secondaryButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[posUi.primaryButton, posUi.modalButtonHalf, confirmDisabled && posUi.disabledCard]}
                onPress={() =>
                  onConfirm({
                    method: settleMethod,
                    note: settleNote,
                    paidDate,
                    checkId: payingWithCheck ? selectedCheckId : null,
                  })
                }
                disabled={confirmDisabled}
                accessibilityRole="button"
                accessibilityLabel="Confirmar pago"
                testID="gastos.settle.confirm"
              >
                <Text style={posUi.primaryButtonText}>{submitting ? 'Guardando…' : 'Confirmar pago'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
        {DiscardConfirmModal}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  infoText: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  fieldWarning: {
    ...typography.caption,
    color: ticketColors.warn,
    marginTop: spacing.xs,
  },
  fieldHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: spacing.xs,
  },
  checkOptionActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.paper2,
  },
  checkOptionSub: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
});
